/**
 * Storico ricalcoli con filtri per unità, anno e stato.
 *
 * Mostra la tabella dei ricalcoli effettuati con badge stato e
 * motivazione rifiuto troncata con tooltip.
 *
 * Importa esclusivamente da @floraltech/ui (regola 6.8 CLAUDE.md).
 */

import {
  Card,
  CardContent,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@floraltech/ui';
import React, { useCallback, useEffect, useState } from 'react';
import { useFormatters } from '@lib/formatters';

/* ─────────────────────────── Utility ─────────────────────────── */

const authHeadersGet = (): Record<string, string> => ({
  Authorization: `Bearer ${localStorage.getItem('token') ?? ''}`,
});

/* ─────────────────────────── Interfacce ─────────────────────────── */

interface Unita {
  id: string;
  nome: string;
}

interface Ricalcolo {
  id: string;
  unita_organizzativa_id: string;
  anno: number;
  mese: number;
  totale_costi_fissi_mensili: string;
  stato: string;
  motivazione_rifiuto: string | null;
  proposto_at: string;
  deciso_at: string | null;
}

/* ─────────────────────────── Badge stato ─────────────────────────── */

const STATI: Record<string, { label: string; className: string }> = {
  proposto: { label: 'Proposto', className: 'bg-accent/10 text-accent' },
  approvato: {
    label: 'Approvato',
    className: 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400',
  },
  rifiutato: { label: 'Rifiutato', className: 'bg-destructive/10 text-destructive' },
};

const StatoBadge: React.FC<{ stato: string }> = ({ stato }) => {
  const cfg = STATI[stato] ?? { label: stato, className: 'bg-muted text-muted-foreground' };
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${cfg.className}`}>
      {cfg.label}
    </span>
  );
};

const MESI_LABEL = [
  '', 'Gennaio', 'Febbraio', 'Marzo', 'Aprile', 'Maggio', 'Giugno',
  'Luglio', 'Agosto', 'Settembre', 'Ottobre', 'Novembre', 'Dicembre',
];

/* ─────────────────────────── Componente ─────────────────────────── */

export const StoricoRicalcoli: React.FC = () => {
  const fmt = useFormatters();
  const [unita, setUnita] = useState<Unita[]>([]);
  const [unitaId, setUnitaId] = useState('');
  const [anno, setAnno] = useState<string>('');
  const [statoFiltro, setStatoFiltro] = useState('');
  const [ricalcoli, setRicalcoli] = useState<Ricalcolo[]>([]);
  const [loading, setLoading] = useState(false);

  /* Carica unità */
  useEffect(() => {
    fetch('/api/v1/unita', { headers: authHeadersGet() })
      .then((r) => r.json())
      .then((data: Unita[]) => setUnita(data))
      .catch(() => {});
  }, []);

  /* Carica ricalcoli */
  const carica = useCallback(async () => {
    setLoading(true);
    const params = new URLSearchParams();
    if (unitaId) params.set('unita_organizzativa_id', unitaId);
    if (anno) params.set('anno', anno);
    if (statoFiltro) params.set('stato', statoFiltro);

    const res = await fetch(`/api/v1/prezzi/ricalcoli?${params}`, {
      headers: authHeadersGet(),
    });
    if (res.ok) setRicalcoli(await res.json());
    setLoading(false);
  }, [unitaId, anno, statoFiltro]);

  useEffect(() => {
    carica();
  }, [carica]);

  const stati = ['', 'proposto', 'approvato', 'rifiutato'];

  return (
    <div className="p-6 space-y-4">
      <h1 className="text-xl font-outfit font-semibold text-foreground">
        Storico ricalcoli
      </h1>

      {/* Filtri */}
      <div className="flex gap-3 flex-wrap">
        <div>
          <label className="text-sm font-medium text-muted-foreground mb-1 block">
            Unità organizzativa
          </label>
          <select
            value={unitaId}
            onChange={(e) => setUnitaId(e.target.value)}
            className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm w-64"
          >
            <option value="">Tutte</option>
            {unita.map((u) => (
              <option key={u.id} value={u.id}>{u.nome}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-muted-foreground mb-1 block">
            Anno
          </label>
          <select
            value={anno}
            onChange={(e) => setAnno(e.target.value)}
            className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm"
          >
            <option value="">Tutti</option>
            {[2024, 2025, 2026, 2027].map((y) => (
              <option key={y} value={y}>{y}</option>
            ))}
          </select>
        </div>
        <div>
          <label className="text-sm font-medium text-muted-foreground mb-1 block">
            Stato
          </label>
          <select
            value={statoFiltro}
            onChange={(e) => setStatoFiltro(e.target.value)}
            className="rounded border border-border bg-card text-foreground px-3 py-1.5 text-sm"
          >
            {stati.map((s) => (
              <option key={s || '__all__'} value={s}>
                {s || 'Tutti'}
              </option>
            ))}
          </select>
        </div>
      </div>

      <Card className="overflow-hidden">
        {loading ? (
          <div className="p-8 text-center text-muted-foreground">Caricamento…</div>
        ) : ricalcoli.length === 0 ? (
          <CardContent className="p-8 text-center text-muted-foreground">
            Nessun ricalcolo trovato
          </CardContent>
        ) : (
          <Table>
            <TableHeader>
              <TableRow className="bg-muted/50">
                <TableHead className="font-medium">Periodo</TableHead>
                <TableHead className="font-medium text-right">Costi fissi mensili</TableHead>
                <TableHead className="font-medium text-center">Stato</TableHead>
                <TableHead className="font-medium">Proposto il</TableHead>
                <TableHead className="font-medium">Deciso il</TableHead>
                <TableHead className="font-medium">Motivazione rifiuto</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {ricalcoli.map((r) => (
                <TableRow key={r.id}>
                  <TableCell className="font-medium">
                    {MESI_LABEL[r.mese]} {r.anno}
                  </TableCell>
                  <TableCell className="text-right">
                    {fmt.currency(r.totale_costi_fissi_mensili)}
                  </TableCell>
                  <TableCell className="text-center">
                    <StatoBadge stato={r.stato} />
                  </TableCell>
                  <TableCell>{fmt.dateTime(r.proposto_at)}</TableCell>
                  <TableCell>
                    {r.deciso_at ? fmt.dateTime(r.deciso_at) : '—'}
                  </TableCell>
                  <TableCell>
                    {r.motivazione_rifiuto ? (
                      <span
                        className="text-sm text-muted-foreground truncate block max-w-48"
                        title={r.motivazione_rifiuto}
                      >
                        {r.motivazione_rifiuto}
                      </span>
                    ) : (
                      '—'
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </Card>
    </div>
  );
};
